import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Archive, ArchiveRestore, Briefcase, Plus, Search } from "lucide-react";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Card } from "@/shared/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { FormField } from "@/shared/ui/form-field";
import { Input } from "@/shared/ui/input";
import { useDateFormat } from "@/shared/hooks/use-date-format";
import { useLanguage } from "@/shared/hooks/use-language";
import { useToast } from "@/shared/hooks/use-toast";
import {
  UNIQUE_ID_MAX_LENGTH,
  deriveUniqueId,
  uniqueIdErrorKey,
  uniqueIdErrorMessage,
} from "@/shared/lib/unique-id";
import { parseBackendErrors } from "@/shared/lib/backend-errors";
import { formatDate } from "@/shared/lib/date-utils";
import { labelOr } from "@/shared/lib/label";
import { ScopeContextStorage } from "@/shared/lib/scope-storage";
import { exitToLogin } from "@/shared/lib/session-exit";
import type { ScopeContext } from "@/features/auth";
import { getInvestors, type Investor } from "@/features/auth/api/investor";
import {
  accountScopeOf,
  createPortfolio,
  getAllPortfolios,
  patchPortfolios,
  type Portfolio,
} from "@/features/portfolio/api/portfolio";
import { isUnreadableResponseError } from "@/shared/lib/response-shape";

const NEW_PORTFOLIO_FIELD_MAP: Record<string, string> = {
  "portfolios.name": "name",
  "portfolios.owner_id": "owner",
  "portfolios.unique_id": "uniqueId",
};

const LISTED_STATUSES = ["active", "frozen"];

const PortfolioList = () => {
  const { t } = useLanguage();
  const { dateFormat } = useDateFormat();
  const navigate = useNavigate();
  const sessionId = ScopeContextStorage.getCurrentSessionId();
  const scopeContext: ScopeContext | null = sessionId
    ? ScopeContextStorage.getScopeContext(sessionId)
    : null;
  const accountScope = scopeContext ? accountScopeOf(scopeContext) : null;
  const scopeMissing = !sessionId || !accountScope;

  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [investors, setInvestors] = useState<Investor[]>([]);
  const [investorsUnavailable, setInvestorsUnavailable] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const [isIncomplete, setIsIncomplete] = useState(false);
  const [load, setLoad] = useState({ includeRemoved: false, token: 0 });
  const [listedIncludeRemoved, setListedIncludeRemoved] = useState(false);
  const [pendingStatusIds, setPendingStatusIds] = useState<string[]>([]);
  const [portfolioToRemove, setPortfolioToRemove] = useState<Portfolio | null>(null);
  const [removalPrompt, setRemovalPrompt] = useState<Portfolio | null>(null);
  const mutationCount = useRef(0);
  const confirmingId = useRef<string | null>(null);

  const [showNewModal, setShowNewModal] = useState(false);
  const [portfolioName, setPortfolioName] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [uniqueId, setUniqueId] = useState("");
  const [uniqueIdEdited, setUniqueIdEdited] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const { toast } = useToast();

  useEffect(() => {
    if (scopeMissing) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    const requested = load.includeRemoved;
    const mutationsAtStart = mutationCount.current;
    setIsLoading(true);
    setError(null);
    getAllPortfolios(sessionId!, accountScope!, {
      statuses: requested ? undefined : LISTED_STATUSES,
      isCancelled: () => cancelled,
    })
      .then((portfolioResponse) => {
        if (cancelled) return;
        // A status change in flight at any point during this read may not be in
        // it, so the read is discarded rather than allowed to resurrect the
        // status the user just left behind.
        if (mutationCount.current !== mutationsAtStart) {
          setLoad((previous) => ({ ...previous, token: previous.token + 1 }));
          return;
        }
        setPortfolios(portfolioResponse.portfolios);
        setIsTruncated(portfolioResponse.isTruncated);
        setIsIncomplete(portfolioResponse.isIncomplete);
        setListedIncludeRemoved(requested);
        setIsLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(t("portfolio.loadFailed"));
        setIsLoading(false);
        // The failed request is abandoned rather than latched: retrying now
        // reloads the filter still on screen, so the working view is always one
        // click away and the toggle can still ask for the other one.
        setLoad((previous) => ({ ...previous, includeRemoved: listedIncludeRemoved }));
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load.token]);

  useEffect(() => {
    if (!sessionId || !accountScope) return;
    let cancelled = false;
    getInvestors(sessionId, accountScope)
      .then((investorResponse) => {
        if (cancelled) return;
        setInvestors(investorResponse.investors || []);
      })
      .catch(() => {
        if (cancelled) return;
        setInvestorsUnavailable(true);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetNewPortfolioForm = () => {
    setPortfolioName("");
    setOwnerId("");
    setUniqueId("");
    setUniqueIdEdited(false);
    setFieldErrors({});
  };

  const toggleNewPortfolioModal = (open: boolean) => {
    if (!open && isCreating) return;
    resetNewPortfolioForm();
    setShowNewModal(open);
  };

  const handleCreate = async () => {
    const trimmedUniqueId = uniqueId.trim();
    const nextFieldErrors: Record<string, string> = {};
    if (!portfolioName.trim()) nextFieldErrors.name = t("portfolio.nameRequired");
    if (!ownerId) nextFieldErrors.owner = t("portfolio.ownerRequired");
    const uniqueIdError = uniqueIdErrorMessage(t, uniqueIdErrorKey(trimmedUniqueId), "portfolio.uniqueIdRequired");
    if (uniqueIdError) nextFieldErrors.uniqueId = uniqueIdError;
    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors);
      return;
    }

    if (!sessionId || !accountScope) return;
    setIsCreating(true);
    try {
      const portfolio = await createPortfolio(sessionId, accountScope, {
        name: portfolioName.trim(),
        ownerId,
        uniqueId: trimmedUniqueId,
      });
      toast({ title: t("portfolio.created") });
      setShowNewModal(false);
      navigate(`/console/portfolio/${portfolio.id}`);
    } catch (err) {
      const { fieldErrors: backendFieldErrors, generalError } = parseBackendErrors(
        err,
        NEW_PORTFOLIO_FIELD_MAP,
      );
      setFieldErrors(backendFieldErrors);
      const message = isUnreadableResponseError(err)
        ? t("errors.unreadableResponse")
        : generalError;
      if (message) {
        toast({ title: message, variant: "destructive" });
      }
    } finally {
      setIsCreating(false);
    }
  };

  const applyStatus = async (
    target: Portfolio,
    status: "active" | "blocked",
    successKey: string,
  ) => {
    if (!sessionId || !accountScope) return;
    setPendingStatusIds((previous) => [...previous, target.id]);
    // Counted at the start as well as at the commit, so a read that resolves
    // before this PATCH does is discarded too: it was taken while the status it
    // reports was already being changed.
    mutationCount.current += 1;
    try {
      const [updatedPortfolio] = await patchPortfolios(sessionId, accountScope, [
        { id: target.id, status },
      ]);
      mutationCount.current += 1;
      setPortfolios((previous) =>
        previous.map((portfolio) =>
          portfolio.id === updatedPortfolio.id
            ? { ...portfolio, status: updatedPortfolio.status }
            : portfolio,
        ),
      );
      toast({ title: t(successKey) });
    } catch (err) {
      const { generalError } = parseBackendErrors(err, {});
      const message = isUnreadableResponseError(err)
        ? t("errors.unreadableResponse")
        : generalError || t("portfolio.statusUpdateFailed");
      toast({ title: message, variant: "destructive" });
    } finally {
      setPendingStatusIds((previous) => previous.filter((id) => id !== target.id));
    }
  };

  const handleRemoveConfirmed = () => {
    if (!portfolioToRemove || confirmingId.current === portfolioToRemove.id) return;
    const target = portfolioToRemove;
    confirmingId.current = target.id;
    setPortfolioToRemove(null);
    applyStatus(target, "blocked", "portfolio.removed")
      .finally(() => {
        if (confirmingId.current === target.id) confirmingId.current = null;
      });
  };

  const openRemoveConfirm = (portfolio: Portfolio) => {
    setRemovalPrompt(portfolio);
    setPortfolioToRemove(portfolio);
  };

  const toggleRemovedView = () =>
    setLoad((previous) => ({
      includeRemoved: !listedIncludeRemoved,
      token: previous.token + 1,
    }));

  const reload = () => setLoad((previous) => ({ ...previous, token: previous.token + 1 }));

  const handleRestore = (portfolio: Portfolio) =>
    applyStatus(portfolio, "active", "portfolio.restored");

  const matchesSearch = (portfolio: Portfolio) =>
    portfolio.name.toLowerCase().includes(searchQuery.toLowerCase());

  const listedPortfolios = listedIncludeRemoved
    ? portfolios
    : portfolios.filter((portfolio) => LISTED_STATUSES.includes(portfolio.status));
  const matchingPortfolios = listedPortfolios.filter(matchesSearch);

  const emptyMessageKey = () => {
    if (listedPortfolios.length > 0) return "portfolio.noSearchMatch";
    if (listedIncludeRemoved) return "portfolio.empty";
    return "portfolio.listedEmpty";
  };

  const statusNoteKey = (status: string) => {
    if (status === "blocked") return "portfolio.removedNote";
    if (status === "frozen") return "portfolio.frozenNote";
    if (status === "closed") return "portfolio.closedNote";
    return null;
  };

  return (
    <main className="flex-1 p-4 md:p-8 animate-fadeIn">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold">{t("portfolio.title")}</h1>
          <p className="text-muted-foreground">{t("portfolio.subtitle")}</p>
        </div>
        <Button onClick={() => toggleNewPortfolioModal(true)} data-testid="portfolio-new">
          <Plus className="w-4 h-4 mr-2" />
          {t("portfolio.new")}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative max-w-sm flex-1 min-w-[14rem]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder={t("portfolio.search")}
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          className="shrink-0"
          data-testid="portfolio-removed-toggle"
          aria-pressed={listedIncludeRemoved}
          aria-busy={isLoading}
          disabled={isLoading}
          onClick={toggleRemovedView}
        >
          <ArchiveRestore className="w-4 h-4 mr-2" />
          {listedIncludeRemoved ? t("portfolio.hideRemoved") : t("portfolio.showRemoved")}
        </Button>
      </div>

      {scopeMissing && (
        <Card className="p-6 border-destructive" data-testid="portfolio-no-scope">
          <p className="text-destructive mb-4">{t("portfolio.noAccountScope")}</p>
          <Button variant="outline" onClick={exitToLogin}>
            {t("portfolio.signInAgain")}
          </Button>
        </Card>
      )}

      {!scopeMissing && error && (
        <Card className="p-6 border-destructive" data-testid="portfolio-list-failed">
          <p className="text-destructive mb-4">{error}</p>
          <Button variant="outline" onClick={reload} data-testid="portfolio-list-retry">
            {t("portfolio.retry")}
          </Button>
        </Card>
      )}

      {!scopeMissing && !isLoading && !error && isIncomplete && (
        <Card className="p-4 mb-4 border-destructive" data-testid="portfolio-incomplete">
          <p className="text-destructive mb-3 text-sm">{t("portfolio.listIncomplete")}</p>
          <Button variant="outline" size="sm" onClick={reload} data-testid="portfolio-incomplete-retry">
            {t("portfolio.retry")}
          </Button>
        </Card>
      )}

      {!scopeMissing && isLoading && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <Card key={index} className="p-6">
              <div className="skeleton-shimmer h-5 w-2/3 rounded bg-muted mb-3" />
              <div className="skeleton-shimmer h-4 w-1/3 rounded bg-muted" />
            </Card>
          ))}
        </div>
      )}

      {!scopeMissing && !isLoading && !error && matchingPortfolios.length === 0 && (
        <Card className="p-10 text-center text-muted-foreground" data-testid="portfolio-empty">
          <Briefcase className="w-8 h-8 mx-auto mb-3" />
          {t(emptyMessageKey())}
        </Card>
      )}

      {!scopeMissing && !isLoading && !error && matchingPortfolios.length > 0 && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {matchingPortfolios.map((portfolio) => (
            <Card
              key={portfolio.id}
              className="p-6 cursor-pointer hover:border-primary transition-colors"
              data-testid="portfolio-card"
              onClick={() => navigate(`/console/portfolio/${portfolio.id}`)}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <h2 className="min-w-0 font-semibold truncate" title={portfolio.name}>
                  {portfolio.name}
                </h2>
                <div
                  className="flex items-center gap-1 shrink-0"
                  onClick={(event) => event.stopPropagation()}
                >
                  <Badge variant={portfolio.status === "active" ? "default" : "secondary"}>
                    {labelOr(t, `status.${portfolio.status}`, portfolio.status)}
                  </Badge>
                  {portfolio.status === "active" && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:text-destructive/90 hover:bg-destructive/10"
                      aria-label={t("portfolio.remove")}
                      title={t("portfolio.removeHint")}
                      data-testid="portfolio-remove"
                      aria-busy={pendingStatusIds.includes(portfolio.id)}
                      disabled={pendingStatusIds.includes(portfolio.id)}
                      onClick={() => openRemoveConfirm(portfolio)}
                    >
                      <Archive className="w-4 h-4" />
                    </Button>
                  )}
                  {portfolio.status === "blocked" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7"
                      title={t("portfolio.restoreHint")}
                      data-testid="portfolio-restore"
                      aria-busy={pendingStatusIds.includes(portfolio.id)}
                      disabled={pendingStatusIds.includes(portfolio.id)}
                      onClick={() => handleRestore(portfolio)}
                    >
                      <ArchiveRestore className="w-4 h-4 mr-1" />
                      {t("portfolio.restore")}
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                {t("portfolio.since")} {formatDate(portfolio.created, dateFormat)}
              </p>
              {portfolio.unique_id && (
                <p
                  className="mt-1 text-xs text-muted-foreground font-mono truncate"
                  title={t("portfolio.uniqueIdLabel")}
                >
                  <span className="sr-only">{t("portfolio.uniqueIdLabel")}: </span>
                  {portfolio.unique_id}
                </p>
              )}
              {statusNoteKey(portfolio.status) && (
                <p className="mt-2 text-xs text-muted-foreground" data-testid="portfolio-status-note">
                  {t(statusNoteKey(portfolio.status)!)}
                </p>
              )}
            </Card>
          ))}
        </div>
      )}

      {!scopeMissing && !isLoading && !error && isTruncated && (
        <p className="mt-4 text-sm text-muted-foreground" data-testid="portfolio-truncated">
          {t("portfolio.truncatedPortfolios")}
        </p>
      )}

      {!scopeMissing && !isLoading && !error && listedIncludeRemoved && (
        <p className="mt-4 text-sm text-muted-foreground" data-testid="portfolio-all-statuses-note">
          {t("portfolio.allStatusesNote")}
        </p>
      )}

      <Dialog
        open={portfolioToRemove !== null}
        onOpenChange={(open) => {
          if (!open) setPortfolioToRemove(null);
        }}
      >
        <DialogContent data-testid="portfolio-remove-confirm">
          <DialogHeader>
            <DialogTitle>{t("portfolio.removeConfirmTitle")}</DialogTitle>
            <DialogDescription>
              {t("portfolio.removeConfirmDescription").replace(
                "{name}",
                () => removalPrompt?.name ?? "",
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPortfolioToRemove(null)}>
              {t("portfolio.cancel")}
            </Button>
            <Button
              variant="destructive"
              data-testid="portfolio-remove-confirm-submit"
              onClick={handleRemoveConfirmed}
            >
              {t("portfolio.remove")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showNewModal} onOpenChange={toggleNewPortfolioModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("portfolio.newTitle")}</DialogTitle>
            <DialogDescription>{t("portfolio.newDescription")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <FormField
              id="portfolio-name"
              label={t("portfolio.name")}
              value={portfolioName}
              error={fieldErrors.name}
              disabled={isCreating}
              onChange={(value: string) => {
                setPortfolioName(value);
                setFieldErrors((prev) => ({ ...prev, name: "" }));
                if (!uniqueIdEdited) {
                  const { derived, errorKey } = deriveUniqueId(value);
                  setUniqueId(derived);
                  setFieldErrors((prev) => ({ ...prev, uniqueId: uniqueIdErrorMessage(t, errorKey, "portfolio.uniqueIdRequired") }));
                }
              }}
            />
            <FormField
              id="portfolio-unique-id"
              label={t("portfolio.uniqueIdLabel")}
              placeholder={t("uniqueId.placeholder")}
              hint={t("uniqueId.hint")}
              value={uniqueId}
              maxLength={UNIQUE_ID_MAX_LENGTH}
              error={fieldErrors.uniqueId}
              disabled={isCreating}
              data-testid="portfolio-create-unique-id"
              onChange={(value: string) => {
                setUniqueId(value);
                setFieldErrors((prev) => ({ ...prev, uniqueId: "" }));
                setUniqueIdEdited(value.length > 0);
              }}
            />
            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="portfolio-owner">
                {t("portfolio.owner")}
              </label>
              <select
                id="portfolio-owner"
                className={`w-full h-10 rounded-md border bg-background px-3 text-sm ${
                  fieldErrors.owner ? "border-destructive" : "border-input"
                }`}
                value={ownerId}
                disabled={isCreating}
                onChange={(event) => {
                  setOwnerId(event.target.value);
                  setFieldErrors((prev) => ({ ...prev, owner: "" }));
                }}
              >
                <option value="">{t("portfolio.ownerPlaceholder")}</option>
                {investors.map((investor) => (
                  <option key={investor.id} value={investor.id}>
                    {investor.name}
                  </option>
                ))}
              </select>
              {investorsUnavailable && (
                <p className="text-xs text-destructive" data-testid="portfolio-investors-unavailable">
                  {t("portfolio.investorsUnavailable")}
                </p>
              )}
              {fieldErrors.owner && (
                <p className="text-xs text-destructive">{fieldErrors.owner}</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => toggleNewPortfolioModal(false)} disabled={isCreating}>
              {t("portfolio.cancel")}
            </Button>
            <Button onClick={handleCreate} disabled={isCreating} data-testid="portfolio-create-submit">
              {isCreating ? t("portfolio.creating") : t("portfolio.create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
};

export default PortfolioList;
